import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTitleAndUser } from '../../../../../hooks/userTitle';
import { Info } from '@phosphor-icons/react';
import { SideModal } from '../../../../../components/SideModal';
import {
  ContentList,
  RowDiv,
  WarningNewMessage,
} from '../../acoesPainelStyles';
import AsideFiltersStocks from './AsideFiltersStocks/index.js';
import api from '../../../../../services/api';
import { financeRoutes } from '../../../../../utils/RoutesApi';
import { CardStockRank } from '../Internacionais/GraphicStocks.jsx';

const PainelContainer = styled.div`
  width: 100%;
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 10px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const StockCard = styled.button`
  background: #fff;
  border: 1px solid #d1d8e0;
  border-radius: 8px;
  overflow: hidden;
  min-height: 150px;
  cursor: pointer;
  text-align: left;
  padding: 0;
  transition: 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  background: #e9eef3;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  color: #1d2d3a;
`;

const CardBody = styled.div`
  padding: 1rem;
`;

const AssetName = styled.p`
  font-size: 0.875rem;
  color: #455a64;
  margin: 0 0 0.5rem;
`;

const AssetInfo = styled.p`
  font-size: 0.75rem;
  color: #78909c;
  margin: 0;
`;

const EmptyMessage = styled.div`
  padding: 1rem 0;
  color: #455a64;
  margin-top: 4rem;
`;

export default function StocksPainel({
  tipo = 'stock',
  isComponent = false,
  ticketActive = '',
  selectedActive = ({ slug, type }) => {},
}) {
  const { setTitle } = useTitleAndUser();
  const [modalFilter, setModalFilter] = useState(false);

  const [stocks, setStocks] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchFilters, setSearchFilters] = useState({
    nome_ativo: '',
    tipo_ativo: tipo,
    currency: '',
    accounting_standard: '',
    sector: '',
    exchange: '',
    isFiltered: false,
  });

  const [loadingMore, setLoadingMore] = useState(false);

  function calculateStockScore(stock) {
    const kpi = stock?.latest_kpi || {};

    const toNumber = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    };

    const clamp = (value, min = 0, max = 100) => {
      return Math.min(Math.max(value, min), max);
    };

    const netMargin = toNumber(kpi.net_margin);
    const ebitdaMargin = toNumber(kpi.ebitda_margin);
    const dividendYield = toNumber(kpi.dy ?? kpi.avg_dy);
    const netDebt = toNumber(kpi.net_debt);
    const netDebtEbitda = toNumber(kpi.net_debt_ebitda);

    const values = [
      netMargin !== null ? clamp(netMargin * 100) : 0,
      ebitdaMargin !== null ? clamp(ebitdaMargin * 100) : 0,
      dividendYield !== null ? clamp(dividendYield * 100) : 0,
      netDebt !== null ? clamp(netDebt <= 0 ? 100 : 50) : 0,
      netDebtEbitda !== null
        ? clamp(netDebtEbitda <= 0 ? 100 : 100 - netDebtEbitda * 20)
        : 0,
    ];

    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!isComponent) {
      setTitle('Stocks');
    }
  }, [isComponent, setTitle]);

  useEffect(() => {
    setSearchFilters((prev) => ({
      ...prev,
      tipo_ativo: tipo,
    }));
  }, [tipo]);

  useEffect(() => {
    function handleScroll() {
      const scrollPosition = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.offsetHeight;

      if (scrollPosition >= pageHeight - 300) {
        loadMoreStocks();
      }
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [stocks, loadingMore, nextPageUrl, searchFilters.nome_ativo, searchFilters.sector, searchFilters.exchange]);

  useEffect(() => {
    async function loadStocks() {
      console.log('Entrou no loadStocks');

      try {
        setLoadingStocks(true);

        const response = await api.get(financeRoutes.INTERNATIONAL_ASSETS, {
          params: {
            asset_type: 'STOCK',
            is_active: true,
            search: searchFilters.nome_ativo || undefined,
            sector: searchFilters.sector || undefined,
            exchange: searchFilters.exchange || undefined,
            page: 1,
            page_size: 21,
          },
        });

        console.log('Stocks internacionais:', response.data);

        const stocksList = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data?.results || [];
        
        const totalCount = response.data?.count || stocksList.length;
        const current = response.data?.current || 1;

        setCurrentPage(current);
        setNextPageUrl(stocksList.length < totalCount ? current + 1 : null);

        const firstStocks = stocksList.slice(0, 21);

        // `latest_kpi` já vem na listagem; o score do ranking é calculado sem
        // buscar o detalhe de cada ativo. O detalhe completo carrega no clique.
        const stocksWithScore = firstStocks.map((stock) => ({
          ...stock,
          // Nota real vinda do backend (asset_score_service); cai na heurística
          // de KPIs só quando o ativo ainda não tem score calculado.
          score: Number(stock.score) || calculateStockScore(stock),
        }));

        // Ordem definida pelo backend (nota desc); preserva a ordem ao paginar.
        setStocks(stocksWithScore);

      } catch (error) {
        console.error('Erro ao buscar stocks internacionais:', error);
        setStocks([]);
      } finally {
        setLoadingStocks(false);
      }
    }

    if (tipo === 'stock') {
      loadStocks();
    }
  }, [tipo, searchFilters.nome_ativo, searchFilters.sector, searchFilters.exchange]);

  async function loadMoreStocks() {
    if (loadingMore) return;
    if (!nextPageUrl) return;

    try {
      setLoadingMore(true);

      const nextPage =
        typeof nextPageUrl === 'number' ? nextPageUrl : currentPage + 1;

      const response = await api.get(financeRoutes.INTERNATIONAL_ASSETS, {
        params: {
          asset_type: 'STOCK',
          is_active: true,
          search: searchFilters.nome_ativo || undefined,
          sector: searchFilters.sector || undefined,
          exchange: searchFilters.exchange || undefined,
          page: nextPage,
          page_size: 21,
        },
      });

      const nextStocks = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.results || [];

      const totalLoaded = stocks.length + nextStocks.length;
      const totalCount = response.data?.count || totalLoaded;
      const current = response.data?.current || nextPage;

      setCurrentPage(current);
      setNextPageUrl(totalLoaded < totalCount ? current + 1 : null);

      const nextStocksWithScore = nextStocks.map((stock) => ({
        ...stock,
        score: calculateStockScore(stock),
      }));

      setStocks((prev) => [...prev, ...nextStocksWithScore]);
    } finally {
      setLoadingMore(false);
    }
  }

  function handleChangeSearch(event) {
    const value = event.target.value;

    setSearchFilters((prev) => ({
      ...prev,
      nome_ativo: value,
      isFiltered: value.length > 0,
    }));
  }

  function handleSelectStock(stock) {
    selectedActive({
      slug: stock.ticker,
      type: 'stock',
    });
  }

  function renderTabTitle() {
    return 'Stocks';
  }

  function renderStocksCards() {
    if (loadingStocks) {
      return <EmptyMessage>Carregando Stocks...</EmptyMessage>;
    }

    if (!filteredStocks.length) {
      return <EmptyMessage>Nenhum Stock encontrado.</EmptyMessage>;
    }

    return (
      <>
        <RowDiv>
          <CardsGrid>
            {filteredStocks.map((stock) => (
              <CardStockRank
                key={stock.id || stock.ticker}
                stock={stock}
                selectedActive={selectedActive}
              />
            ))}
          </CardsGrid>
        </RowDiv>
        <div style={{marginTop:'2rem'}}>
          {loadingMore && <EmptyMessage>Carregando mais Stocks...</EmptyMessage>}
        </div>
      </>
    );
  }

  const filteredStocks = stocks.filter((stock) => {
    const search = searchFilters.nome_ativo?.toLowerCase() || '';

    const matchesSearch =
      !search ||
      stock.name?.toLowerCase().includes(search) ||
      stock.ticker?.toLowerCase().includes(search);

    const matchesCurrency =
      !searchFilters.currency ||
      stock.currency === searchFilters.currency;

    const matchesAccounting =
      !searchFilters.accounting_standard ||
      stock.accounting_standard === searchFilters.accounting_standard;

    // Setor e bolsa são filtrados no backend (com o de-para das variantes de
    // setor e o agrupamento de códigos de bolsa). Não refiltrar aqui — a
    // comparação exata desfaria o de-para (ex.: 'Saúde' x 'Cuidados com a
    // Saúde', 'NYSE' x 'NYS'/'NYQ').
    return matchesSearch && matchesCurrency && matchesAccounting;
  });

  if (tipo !== 'stock') {
    return null;
  }

  return (
    <div style={{ marginTop: '0%', background: 'transparent' }}>
      <ContentList>
        <div className="backPage" style={{ zIndex: '1' }}>
          <PainelContainer>
            {!isComponent && (
              <>
                <AsideFiltersStocks
                  tipo="stock"
                  setSearchFilters={setSearchFilters}
                  searchFilters={searchFilters}
                  onChangeSearch={handleChangeSearch}
                  searchPlaceholder={`Buscar em ${renderTabTitle()}`}
                  showMobileButton={window.screen.width < 1100}
                  onOpenMobileFilters={() => setModalFilter(true)}
                />

                <br />

                <WarningNewMessage>
                  <Info color="#EC6643" size={24} />
                  <p>
                    O Ranking tem como objetivo orientar você a iniciar seus
                    estudos pelos ativos mais relevantes. No entanto, ele não
                    deve ser interpretado como uma recomendação específica de
                    compra ou venda.
                  </p>
                </WarningNewMessage>
              </>
            )}

            {renderStocksCards()}
          </PainelContainer>
        </div>
      </ContentList>

      <SideModal
        id="sideFilterInternational"
        isOpen={modalFilter}
        setState={setModalFilter}
      >
        <AsideFiltersStocks
          setSearchFilters={setSearchFilters}
          searchFilters={searchFilters}
          onChangeSearch={handleChangeSearch}
          searchPlaceholder={`Buscar em ${renderTabTitle()}`}
          showMobileButton={false}
          onOpenMobileFilters={() => setModalFilter(true)}
        />
      </SideModal>
    </div>
  );
}